/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Loader } from '@quero-delivery/quero-components-web';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';

import usePlacesMinimal from '@/hooks/place/usePlacesMinimal';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import DedicatePlace from '@/features/dedicate-place';
import useMotoboyDedication from '@/hooks/motoboy/useMotoboyDedication';

import styles from './styles.module.scss';
import { dialog } from '@/infra/dialog/Dialog';
import { caseInsensitiveSearch } from './utils';
import { DELIVERYMAN_NOT_FOUND_TOAST } from '@/_features/motoboys/constants';

function DedicateMotoboy() {
  const navigate = useNavigate();

  const { motoboyId } = useParams<{ motoboyId: string }>();

  const { motoboy, loadingMotoboy, errorsMotoboy } = useMotoboy(
    motoboyId || '',
  );

  const {
    dedicationIsLoading,
    placesDedicated,
    lastMotoboyIdDedication,
    handleDeleteDedicationByPlaceId,
  } = useMotoboyDedication(motoboyId);

  const [chosenPlaces, setChosenPlaces] = useState<string[]>([]);

  const {
    placesMinimalOptions,
    placesMinimal,
    isLoading: isLoadingPLacesMinimal,
    error: errorsPlacesMinimal,
  } = usePlacesMinimal();

  const chosenPlacesMinimal = useMemo(() => {
    return chosenPlaces.map(placeId =>
      placesMinimal.find(place => place._id === placeId),
    );
  }, [chosenPlaces, placesMinimal]);

  useEffect(() => {
    if (lastMotoboyIdDedication === motoboyId) {
      const placesToAdd = placesDedicated.filter(
        place => !chosenPlaces.some(chosenPlace => chosenPlace === place),
      );

      setChosenPlaces(value => [...placesToAdd, ...value]);
    }
  }, [placesDedicated]);

  async function onDeleteDedication(placeId: string) {
    const placeDeleted = chosenPlacesMinimal.find(
      placeMinimal => placeMinimal?._id === placeId,
    );

    if (!placeDeleted) {
      return;
    }

    const confirmed = await dialog.confirm(
      <div className="text-center">
        <p className="mgn-bottom-sm">
          Você está tentando excluir o <b>{placeDeleted?.nomeExibicao}</b> da
          dedicação do entregador
        </p>

        <h3>Você tem certeza que quer excluir?</h3>
      </div>,
    );

    if (!confirmed) {
      return;
    }

    const newPlacesChoosed = chosenPlaces.filter(place => place !== placeId);

    setChosenPlaces(newPlacesChoosed);

    if (!placesDedicated.includes(placeDeleted._id)) {
      return;
    }

    await handleDeleteDedicationByPlaceId(placeDeleted);
  }

  if (!motoboy && !isEmpty(errorsMotoboy)) {
    toast.warn(DELIVERYMAN_NOT_FOUND_TOAST.message, {
      toastId: DELIVERYMAN_NOT_FOUND_TOAST.toastId,
    });
    return <Navigate to="/entregadores" />;
  }

  if (loadingMotoboy || !motoboy) {
    return (
      <div className="loading-container">
        <Loader />
      </div>
    );
  }

  if (lastMotoboyIdDedication !== motoboyId) {
    return (
      <div className="loading-container">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="header-container">
        <div className="back-label" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} size="lg" />
          <strong>Voltar</strong>
        </div>
      </div>

      <div className={styles.choosePlaces}>
        <Dropdown
          label="Estabelecimentos"
          placeholder="digite o nome do estabelecimento"
          labelStyle={{ fontWeight: 'bold' }}
          value={chosenPlaces}
          onChange={(event, data) => {
            const target = event.target as any;

            if (target.className.includes('delete')) {
              const deletedPlaceId = chosenPlaces.find(
                placeId => !(data.value as string[]).includes(placeId),
              );

              if (deletedPlaceId) {
                onDeleteDedication(deletedPlaceId);
                return;
              }
            }

            setChosenPlaces(data.value as string[]);
          }}
          options={placesMinimalOptions}
          loading={isLoadingPLacesMinimal}
          disabled={isLoadingPLacesMinimal || dedicationIsLoading}
          errorMessage={errorsPlacesMinimal}
          multiple
          search={caseInsensitiveSearch as any}
          noResultsMessage="Nenhum estabelecimento disponível"
          icon="search"
        />
      </div>

      <div>
        {isEmpty(chosenPlacesMinimal) ? (
          <p>Não existe dedicação para o entregador</p>
        ) : (
          chosenPlacesMinimal.map(place =>
            place ? (
              <DedicatePlace
                key={place._id}
                place={place}
                onDeleteDedication={onDeleteDedication}
              />
            ) : null,
          )
        )}
      </div>
    </div>
  );
}

export { DedicateMotoboy };
